import { useState, useEffect, useRef, useCallback } from "react";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const TYPE_CONFIG = {
  intervention:{ ic:"🔧", label:"Intervention", color:"#5b6ef8" },
  client:      { ic:"👤", label:"Client",       color:"#22d3a0" },
  ticket:      { ic:"🎫", label:"SAV",           color:"#ef4444" },
  stock:       { ic:"📦", label:"Stock",         color:"#a855f7" },
};

const STATUT_LABELS = {
  pending:"Planifié",planifiee:"Planifié","in-progress":"En cours",en_cours:"En cours",
  completed:"Terminé",terminee:"Terminé",cancelled:"Annulé",annulee:"Annulé",
  ouvert:"Ouvert",resolu:"Résolu",ok:"OK",alerte:"Faible",critique:"Critique",
};

export default function SearchBar({ T, onSelectInt, onSelectClient, onNavigate }) {
  const [query, setQuery]   = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]     = useState(false);
  const [focused, setFocused] = useState(0);
  const containerRef = useRef(null);
  const inputRef     = useRef(null);
  const timer        = useRef(null);
  const isSearching  = useRef(false);

  // Fermer si clic en dehors
  useEffect(()=>{
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return ()=>document.removeEventListener("mousedown", handler);
  },[]);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    isSearching.current = true;
    try {
      const res = await fetch(`${BASE}/search?q=${encodeURIComponent(q)}`,{headers:h()}).then(r=>r.json());
      setResults(res.results||[]);
      setOpen(true);
      setFocused(0);
    } catch(e) {}
    setLoading(false);
    isSearching.current = false;
  },[]);

  const onChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(timer.current);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(()=>doSearch(q), 300);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key==="ArrowDown") { e.preventDefault(); setFocused(f=>Math.min(f+1,results.length-1)); }
    if (e.key==="ArrowUp")   { e.preventDefault(); setFocused(f=>Math.max(f-1,0)); }
    if (e.key==="Enter"&&results[focused]) { e.preventDefault(); select(results[focused]); }
    if (e.key==="Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  const select = (item) => {
    setOpen(false);
    setQuery("");
    setResults([]);
    inputRef.current?.blur();
    if (item.type==="intervention") onSelectInt&&onSelectInt(item);
    else if (item.type==="client")  onSelectClient&&onSelectClient(item);
    else if (item.type==="ticket")  onNavigate&&onNavigate("sav");
    else if (item.type==="stock")   onNavigate&&onNavigate("stock");
  };

  const clear = (e) => {
    e.stopPropagation();
    setQuery(""); setResults([]); setOpen(false);
    inputRef.current?.focus();
  };

  const groups = ["intervention","client","ticket","stock"]
    .map(type=>({ type, items: results.filter(r=>r.type===type) }))
    .filter(g=>g.items.length>0);

  return (
    <div ref={containerRef} style={{position:"relative",flex:1,maxWidth:420}}>
      {/* Input */}
      <div style={{display:"flex",alignItems:"center",gap:8,background:T.bg,border:`1.5px solid ${open?T.accent:T.border}`,borderRadius:11,padding:"7px 12px",transition:"border 0.2s",cursor:"text"}}
        onClick={()=>inputRef.current?.focus()}>
        <span style={{fontSize:14,color:T.muted,flexShrink:0}}>{loading?"⏳":"🔍"}</span>
        <input
          ref={inputRef}
          value={query}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={()=>{ if(query.length>=2&&results.length>0) setOpen(true); }}
          placeholder="Rechercher interventions, clients, SAV..."
          style={{background:"none",border:"none",outline:"none",color:T.text,fontSize:13,flex:1,minWidth:0}}
        />
        {query&&(
          <button onMouseDown={clear} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:15,lineHeight:1,flexShrink:0,padding:"2px"}}>✕</button>
        )}
      </div>

      {/* Dropdown résultats */}
      {open&&results.length>0&&(
        <div style={{position:"absolute",top:"calc(100% + 6px)",left:0,right:0,background:T.dark?"#1e2435":T.card,border:`1px solid ${T.border}`,borderRadius:14,boxShadow:"0 16px 48px rgba(0,0,0,0.6)",zIndex:9999,maxHeight:440,overflowY:"auto"}}>
          {groups.map(group=>{
            const cfg = TYPE_CONFIG[group.type];
            return(
              <div key={group.type}>
                <div style={{padding:"8px 14px 4px",fontSize:10,fontWeight:800,color:cfg.color,textTransform:"uppercase",letterSpacing:"0.7px",background:`${cfg.color}08`,borderBottom:`1px solid ${cfg.color}20`}}>
                  {cfg.ic} {cfg.label}s — {group.items.length} résultat(s)
                </div>
                {group.items.map(item=>{
                  const globalIdx = results.indexOf(item);
                  const isFoc = focused===globalIdx;
                  return(
                    <div key={item.id}
                      onMouseDown={()=>select(item)}
                      onMouseEnter={()=>setFocused(globalIdx)}
                      style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",cursor:"pointer",background:isFoc?`${cfg.color}18`:"transparent",borderBottom:`1px solid ${T.border}18`,transition:"background 0.1s"}}>
                      <div style={{width:34,height:34,borderRadius:9,background:`${cfg.color}22`,border:`1.5px solid ${cfg.color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>
                        {cfg.ic}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:isFoc?cfg.color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {item.label}
                        </div>
                        <div style={{fontSize:11,color:T.muted,display:"flex",gap:8}}>
                          {item.reference&&<span style={{fontFamily:"monospace"}}>{item.reference}</span>}
                          {item.sub&&<span>{item.sub}</span>}
                          {item.statut&&<span style={{color:cfg.color,fontWeight:600}}>{STATUT_LABELS[item.statut]||item.statut}</span>}
                        </div>
                      </div>
                      <span style={{color:T.muted,fontSize:12,flexShrink:0}}>→</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div style={{padding:"7px 14px",fontSize:10,color:T.muted,borderTop:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between"}}>
            <span>{results.length} résultat(s)</span>
            <span>↑↓ Naviguer · Entrée Sélectionner · Échap Fermer</span>
          </div>
        </div>
      )}

      {/* Aucun résultat */}
      {open&&query.length>=2&&!loading&&results.length===0&&(
        <div style={{position:"absolute",top:"calc(100% + 6px)",left:0,right:0,background:T.dark?"#1e2435":T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"20px",textAlign:"center",zIndex:9999,boxShadow:"0 16px 48px rgba(0,0,0,0.6)"}}>
          <div style={{fontSize:24,marginBottom:8}}>🔍</div>
          <div style={{fontSize:13,color:T.muted}}>Aucun résultat pour "<strong style={{color:T.text}}>{query}</strong>"</div>
        </div>
      )}
    </div>
  );
}
